/**
 * Policy Details API Service
 * Provides functions to interact with the policy details API
 * for fetching trade policy information by country, mineral, and year
 */

import { API_BASE_URL as POLICY_DETAILS_API_BASE } from './apiConfig';

/**
 * Get policy details for a specific country, mineral, and year
 * @param {Object} options - Filter options
 * @param {string} options.countryCode - Filter by country code
 * @param {string} options.countryName - Filter by country name
 * @param {string} options.mineral - Filter by mineral name
 * @param {number} options.year - Filter by year
 * @param {number} options.limit - Maximum number of results (default: 50)
 * @returns {Promise<Object>} Policy details data
 */
export const getPolicyDetails = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (options.countryCode) params.append('country_code', options.countryCode);
    if (options.countryName) params.append('country_name', options.countryName);
    if (options.mineral) params.append('mineral', options.mineral);
    if (options.year) params.append('year', options.year);
    if (options.limit) params.append('limit', options.limit);
    
    const response = await fetch(`${POLICY_DETAILS_API_BASE}/api/policy-details?${params}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching policy details:', error);
    throw error;
  }
};

/**
 * Get policy details for a specific country on the map
 * @param {string} countryName - Country name
 * @param {string} mineral - Mineral name
 * @param {number} year - Year
 * @returns {Promise<Array>} Array of policy details for the country
 */
export const getCountryPolicyDetails = async (countryName = null, mineral = null, year = null) => {
  try {
    const data = await getPolicyDetails({
      countryName,
      mineral,
      year,
      limit: 100
    });
    
    if (data.success && data.data.length > 0) {
      // Return the policies for the first matching country
      return data.data[0].policies || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching country policy details:', error);
    return [];
  }
};

/**
 * Get trade restrictions for a specific country and mineral
 * @param {string} countryName - Country name
 * @param {string} mineral - Mineral name
 * @param {number} year - Year
 * @returns {Promise<Array>} Array of trade restriction policies
 */
export const getTradeRestrictions = async (countryName, mineral, year) => {
  try {
    const policies = await getCountryPolicyDetails(countryName, mineral, year);
    
    // Filter for policies with trade restrictions
    return policies.filter(policy => policy.has_trade_restriction);
  } catch (error) {
    console.error('Error fetching trade restrictions:', error);
    return [];
  }
};

/**
 * Get policy summary for a country
 * @param {string} countryName - Country name
 * @param {string} mineral - Mineral name
 * @param {number} year - Year
 * @returns {Promise<Object>} Policy summary data
 */
export const getPolicySummary = async (countryName = null, mineral = null, year = null) => {
  try {
    const policies = await getCountryPolicyDetails(countryName, mineral, year);
    
    const summary = {
      totalPolicies: policies.length,
      tradeRestrictions: policies.filter(p => p.has_trade_restriction).length,
      policyTypes: {},
      restrictionTypes: {},
      riskLevels: {},
      recentPolicies: []
    };
    
    // Analyze policy types
    policies.forEach(policy => {
      if (policy.policy_type) {
        summary.policyTypes[policy.policy_type] = (summary.policyTypes[policy.policy_type] || 0) + 1;
      }
      
      if (policy.restriction_type) {
        summary.restrictionTypes[policy.restriction_type] = (summary.restrictionTypes[policy.restriction_type] || 0) + 1;
      }
      
      if (policy.risk_level) {
        summary.riskLevels[policy.risk_level] = (summary.riskLevels[policy.risk_level] || 0) + 1;
      }
    });
    
    // Get recent policies (last 5)
    summary.recentPolicies = policies
      .filter(p => p.policy_title)
      .sort((a, b) => new Date(b.created_date) - new Date(a.created_date))
      .slice(0, 5);
    
    return summary;
  } catch (error) {
    console.error('Error fetching policy summary:', error);
    return {
      totalPolicies: 0,
      tradeRestrictions: 0,
      policyTypes: {},
      restrictionTypes: {},
      riskLevels: {},
      recentPolicies: []
    };
  }
};

/**
 * Format policy data for display
 * @param {Object} policy - Policy object
 * @returns {Object} Formatted policy data
 */
export const formatPolicyForDisplay = (policy) => {
  return {
    id: policy.id,
    title: policy.policy_title || 'No Title',
    description: policy.policy_description || 'No Description',
    status: policy.policy_status || 'Unknown',
    type: policy.policy_type || 'Unknown',
    jurisdiction: policy.jurisdiction_level || 'Unknown',
    enacted: policy.date_enacted || 'Unknown',
    repealed: policy.date_repealed || null,
    source: policy.policy_source || 'Unknown',
    referenceUrl: policy.reference_url || null,
    hasRestriction: policy.has_trade_restriction || false,
    restrictionType: policy.restriction_type || null,
    riskLevel: policy.risk_level || 'Unknown',
    riskScore: policy.partner_risk_score || 0,
    indiaRole: policy.india_role || 'Unknown'
  };
};

/**
 * Format trade restriction data for display
 * @param {Object} policy - Policy object with trade restriction
 * @returns {Object} Formatted trade restriction data
 */
export const formatTradeRestrictionForDisplay = (policy) => {
  return {
    id: policy.id,
    title: policy.policy_title || 'No Title',
    restrictionType: policy.restriction_type || 'Unknown',
    measureType: policy.measure_type || 'Unknown',
    purpose: policy.purpose_of_measure || 'No Purpose Specified',
    source: policy.measure_source || policy.policy_source || 'Unknown',
    enacted: policy.date_enacted || 'Unknown',
    status: policy.policy_status || 'Unknown',
    referenceUrl: policy.reference_url || null,
    riskLevel: policy.risk_level || 'Unknown',
    riskScore: policy.partner_risk_score || 0
  };
};

const policyDetailsApi = {
  getPolicyDetails,
  getCountryPolicyDetails,
  getTradeRestrictions,
  getPolicySummary,
  formatPolicyForDisplay,
  formatTradeRestrictionForDisplay,
};

export default policyDetailsApi;
